import { motion, useReducedMotion } from 'framer-motion'
import MathField from './MathField'

/* ── The hero's space ────────────────────────────────────────────────────
   Everything behind the hero copy, furthest back first: a teal light, two
   tilted orbits with a travelling spark, the four-point stars from the logo,
   and the floating mathematics. Each sits on its own `.depth-layer`, so
   scroll and pointer parallax pull them apart into depth. All of it is
   hairline and teal — engineered rather than decorative — and the whole
   scene fades out at the hero's foot. Purely decorative. */

const EASE = [0.2, 0, 0, 1]

// Tilted orbits around the capability sheet, a spark riding each.
export function Orbits({ reduce, id = 'hero' }) {
  const rings = [
    { rx: 300, ry: 96, dur: 18, o: 0.45 },
    { rx: 360, ry: 132, dur: 26, o: 0.25, dash: '2 7' },
  ]
  return (
    <svg viewBox="-400 -220 800 440" className="h-full w-full overflow-visible" aria-hidden="true">
      <defs>
        <radialGradient id={`${id}-spark`}>
          <stop offset="0" stopColor="white" />
          <stop offset="0.3" stopColor="rgb(var(--primary))" stopOpacity="0.8" />
          <stop offset="1" stopColor="rgb(var(--primary))" stopOpacity="0" />
        </radialGradient>
      </defs>
      {rings.map((o, i) => {
        const d = `M${-o.rx} 0a${o.rx} ${o.ry} 0 1 0 ${o.rx * 2} 0a${o.rx} ${o.ry} 0 1 0 ${-o.rx * 2} 0`
        return (
          <g key={i} transform="rotate(-16)">
            <motion.path
              id={`${id}-orbit-${i}`}
              d={d}
              fill="none"
              stroke="rgb(var(--primary))"
              strokeOpacity={o.o}
              strokeWidth="1"
              strokeDasharray={o.dash}
              initial={reduce ? false : { pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 1.6, ease: EASE, delay: 0.3 + i * 0.2 }}
            />
            {!reduce && (
              <circle r="10" fill={`url(#${id}-spark)`}>
                <animateMotion dur={`${o.dur}s`} repeatCount="indefinite" begin={`${1.4 + i * 0.6}s`}>
                  <mpath href={`#${id}-orbit-${i}`} />
                </animateMotion>
              </circle>
            )}
          </g>
        )
      })}
    </svg>
  )
}

// The logo's four-point star.
export const Star = ({ size }) => (
  <svg viewBox="-10 -10 20 20" width={size} height={size} aria-hidden="true">
    <path d="M0-10C1 -2 2 -1 10 0C2 1 1 2 0 10C-1 2 -2 1 -10 0C-2 -1 -1 -2 0 -10Z" fill="currentColor" />
  </svg>
)

// [position classes, size px, tone, depth]
const STARS = [
  ['left-[4%] top-[9rem]', 14, 'text-primary', 1.2],
  ['left-[40%] top-[7.5rem] hidden lg:block', 10, 'text-secondary', 1.6],
  ['right-[3%] top-[22rem] hidden lg:block', 16, 'text-primary', 0.6],
  ['left-[52%] bottom-[7rem] hidden lg:block', 12, 'text-primary', 0.9],
  ['right-[38%] top-[6.5rem] hidden md:block', 8, 'text-secondary', 1.4],
]

export default function HeroScene() {
  const reduce = useReducedMotion()
  return (
    <div className="scene-fade pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
      {/* Furthest: light pooled behind the sheet, and a cooler one top-left. */}
      <div className="depth-layer" style={{ '--depth': 1.3 }}>
        <div className="wash wash-primary right-[-6rem] top-[3rem] h-[34rem] w-[40rem] lg:right-[2%]" />
        <div className="wash wash-navy left-[-10rem] top-[-8rem] h-[26rem] w-[34rem]" />
      </div>

      {/* The orbits, wide and slow, passing behind the sheet. */}
      <div className="depth-layer" style={{ '--depth': 1 }}>
        <div className="absolute right-[-20rem] top-[7rem] hidden h-[28rem] w-[54rem] opacity-50 md:block md:right-[-12rem] lg:right-[-7rem] lg:top-[5rem] lg:h-[36rem] lg:w-[60rem] lg:opacity-100">
          <Orbits reduce={reduce} />
        </div>
      </div>

      {/* Stars from the logo, each at its own depth. */}
      {STARS.map(([pos, size, tone, depth], i) => (
        <div key={i} className="depth-layer" style={{ '--depth': depth }}>
          <motion.div
            className={`absolute ${pos} ${tone}`}
            initial={reduce ? false : { opacity: 0, scale: 0.5 }}
            animate={{ opacity: 0.55, scale: 1 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.6 + i * 0.12 }}
          >
            <span className="star-twinkle block" style={{ animationDelay: `${i * -1.3}s` }}>
              <Star size={size} />
            </span>
          </motion.div>
        </div>
      ))}

      {/* Nearest: the mathematics. */}
      <MathField />
    </div>
  )
}
